/**
 * Lazy, in-view video playback for the Ultra sections.
 *
 * Any <video data-ultra-video> is
 *   - muted + playsinline (so browsers allow autoplay),
 *   - loaded lazily: <source data-src="…"> is swapped to src when it nears the viewport,
 *   - played while at least 20% visible, paused otherwise,
 *   - paused while hidden by CSS (e.g. the mobile video on desktop).
 * Add data-ultra-video="always" to keep playing even when off screen.
 */
(function () {
  if (window.__ultraVideoReady) return;
  window.__ultraVideoReady = true;

  function loadSources(video) {
    if (video.dataset.sourcesReady === 'true') return;
    let changed = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      if (!source.getAttribute('src')) {
        source.setAttribute('src', source.dataset.src);
        changed = true;
      }
    });
    if (changed) {
      try {
        video.load();
      } catch (error) {
        /* ignore */
      }
    }
    video.dataset.sourcesReady = 'true';
  }

  function isHidden(video) {
    return video.offsetParent === null && getComputedStyle(video).position !== 'fixed';
  }

  function play(video) {
    if (isHidden(video)) return;
    loadSources(video);
    video.muted = true;
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') promise.catch(() => {});
  }

  function init(video) {
    if (video.dataset.ultraVideoInit) return;
    video.dataset.ultraVideoInit = 'true';
    video.muted = true;
    video.setAttribute('playsinline', '');
    if (!video.hasAttribute('controls') && !video.hasAttribute('loop')) video.loop = true;

    if (!('IntersectionObserver' in window)) {
      play(video);
      return;
    }

    const always = video.dataset.ultraVideo === 'always';
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) play(video);
          else if (!always) video.pause();
          if (entry.isIntersecting) loadSources(video);
        });
      },
      { rootMargin: '150px 0px', threshold: [0, 0.2, 0.5] }
    );
    observer.observe(video);
  }

  function scan(root) {
    (root || document).querySelectorAll('video[data-ultra-video]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', () => scan());
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  if (document.readyState !== 'loading') scan();

  const mq = window.matchMedia('(max-width: 920px)');
  const resync = () =>
    document.querySelectorAll('video[data-ultra-video]').forEach((video) => {
      if (isHidden(video)) video.pause();
      else play(video);
    });
  if (mq.addEventListener) mq.addEventListener('change', resync);
})();
